import {
  DraggableTab,
  draggableTabVariants,
  type DraggableTabVariantProps,
} from '@/app/_common/split-view/draggable-tab'
import { NavExpandIcon } from '@/components/icons/nav-expand-icon'
import { PlusIcon } from '@/components/icons/plus-icon'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useDndContext, useDroppable } from '@dnd-kit/core'
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import { TabsPanelId, useSplitView } from './split-view-context'
import { renderTabContent } from './tabs/content/_render-tab-content'
import { createStaticTab } from './tabs/factories'
import { TAB_TYPE_CHAT, TabTypeStatic } from './tabs/types'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

type DraggableTabsProps = Readonly<{
  panelId: TabsPanelId
}>

const ACTIVE_TAB_WIDTH = 200
const MIN_INACTIVE_TAB_WIDTH = 100

const DraggableTabs = ({ panelId }: DraggableTabsProps) => {
  const tabsMap = React.useRef<Map<string, HTMLButtonElement>>(new Map())
  const { setNodeRef, over, isOver } = useDroppable({ id: panelId })
  const { activeNodeRect: draggingNodeRect, active: dragging } = useDndContext()
  const { enableSplitView, getPanelState, setActiveTabForPanel, toggleSplitView, addTab } =
    useSplitView()
  const { tabs, activeTabId } = getPanelState({ panelId })
  const draggingTabId = dragging?.id ? String(dragging.id) : null
  const overId = over?.id ? String(over.id) : null
  const tabsIdArray = React.useMemo(
    () => tabs.filter((t) => Boolean(t?.id)).map((t) => t.id),
    [tabs],
  )
  const isOverOriginList = tabs.some((t) => t.id === draggingTabId)

  const getTabVariant = (id: string, isLast: boolean): DraggableTabVariantProps['variant'] => {
    if (id === overId || (isLast && isOver && isOverOriginList)) return 'dropTarget'
    if (id === draggingTabId) return 'ghost'
    if (id === activeTabId) return 'active'
    return 'default'
  }

  const handleActiveTabChange = React.useCallback(
    (tabId: string) => {
      setActiveTabForPanel(panelId, tabId)
    },
    [setActiveTabForPanel, panelId],
  )

  const showExpandButton = React.useMemo(() => {
    if (enableSplitView) return panelId === TabsPanelId.Right
    return panelId === TabsPanelId.Left
  }, [enableSplitView, panelId])

  const containerRef = React.useRef<HTMLDivElement>(null)
  const [showScrollButtons, setShowScrollButtons] = React.useState(false)
  const [inactiveTabWidth, setInactiveTabWidth] = React.useState(ACTIVE_TAB_WIDTH)

  // Recalculate inactive tab widths whenever tabs or active tab changes
  React.useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const calculateWidths = () => {
      const containerWidth = container.clientWidth
      const activeWidth = ACTIVE_TAB_WIDTH
      const inactiveCount = Math.max(tabs.length - 1, 0)

      if (inactiveCount === 0) {
        setInactiveTabWidth(ACTIVE_TAB_WIDTH)
        setShowScrollButtons(false)
        return
      }

      // Account for scroll button widths when calculating available space
      const scrollButtonSpace = showScrollButtons ? 36 : 0 // 2 buttons × 7px width + gaps
      const totalDesiredWidth = activeWidth + inactiveCount * ACTIVE_TAB_WIDTH

      if (totalDesiredWidth <= containerWidth - scrollButtonSpace) {
        setInactiveTabWidth(ACTIVE_TAB_WIDTH)
        setShowScrollButtons(false)
      } else {
        const available = containerWidth - activeWidth - scrollButtonSpace
        const newWidth = Math.max(MIN_INACTIVE_TAB_WIDTH, available / inactiveCount)
        setInactiveTabWidth(newWidth)
        setShowScrollButtons(true)
      }
    }

    calculateWidths()

    const resizeObserver = new ResizeObserver(calculateWidths)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [tabs.length, activeTabId, showScrollButtons])

  const scrollBy = (distance: number) => {
    containerRef.current?.scrollBy({ left: distance, behavior: 'smooth' })
  }

  // Focus and scroll to active tab
  React.useEffect(() => {
    if (activeTabId && tabsMap.current.has(activeTabId)) {
      const tabElement = tabsMap.current.get(activeTabId)
      const frame = requestAnimationFrame(() => {
        tabElement?.focus()
        // Scroll the tab into view
        tabElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      })
      return () => cancelAnimationFrame(frame)
    }
  }, [activeTabId])

  return (
    <SortableContext items={tabsIdArray} strategy={horizontalListSortingStrategy}>
      <Tabs
        className='flex h-full flex-col bg-neutral-75'
        value={activeTabId}
        onValueChange={handleActiveTabChange}
      >
        <TabsList
          ref={setNodeRef}
          className='flex w-full shrink-0 items-center gap-2 bg-neutral-75'
        >
          {/* Left Scroll */}
          <AnimatePresence initial={false}>
            {showScrollButtons && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 28 }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Button
                  unstyled
                  onClick={() => scrollBy(-150)}
                  className='flex h-9 w-7 shrink-0 items-center justify-center rounded'
                >
                  <ChevronLeftIcon className='size-4' />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Scrollable Tabs */}
          <div className='flex h-9 flex-1 items-center overflow-hidden'>
            <div
              ref={containerRef}
              className={cn(
                'scrollbar-hidden flex h-full min-w-0 snap-x snap-mandatory gap-1 overflow-x-auto scroll-smooth',
                draggingTabId && 'overflow-x-hidden',
              )}
            >
              <AnimatePresence initial={false}>
                {tabs.map((tab, index) => {
                  const isActive = tab.id === activeTabId
                  const width = isActive ? ACTIVE_TAB_WIDTH : inactiveTabWidth
                  return (
                    <motion.div
                      key={`${tab.id}-${tab.type}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.225, ease: 'easeInOut' }}
                      style={{ width }}
                      className='shrink-0 snap-start overflow-hidden whitespace-nowrap'
                    >
                      <DraggableTab
                        tab={tab}
                        variant={getTabVariant(tab.id, index === tabs.length - 1)}
                        ref={(el) => {
                          if (el) tabsMap.current.set(tab.id, el)
                          else tabsMap.current.delete(tab.id)
                        }}
                      />
                    </motion.div>
                  )
                })}
              </AnimatePresence>

              {isOver && !isOverOriginList && (
                <div
                  style={{
                    width: draggingNodeRect?.width,
                    height: draggingNodeRect?.height,
                  }}
                  className={draggableTabVariants({ variant: 'dropTarget' })}
                />
              )}

              {/* Plus Button */}
              <Button
                unstyled
                aria-label='Open a new tab'
                className='mx-1 flex size-7 shrink-0 items-center justify-center self-center rounded-full outline-none transition-colors ease-in-out hover:bg-neutral-25/50 focus:bg-neutral-25/50 active:bg-neutral-25/50'
                onClick={() => {
                  addTab({ tab: createStaticTab(TabTypeStatic.Blank), panelId, index: tabs.length })
                }}
              >
                <PlusIcon className='size-3 shrink-0 text-neutral-150' />
              </Button>
            </div>
          </div>

          {/* Right Scroll */}
          <AnimatePresence initial={false}>
            {showScrollButtons && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 28 }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Button
                  unstyled
                  onClick={() => scrollBy(150)}
                  className='flex h-9 w-7 shrink-0 items-center justify-center rounded'
                >
                  <ChevronRightIcon className='size-4' />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Expand / Collapse */}
          {showExpandButton && (
            <Tooltip delayDuration={500}>
              <TooltipTrigger asChild>
                <Button
                  unstyled
                  className='mr-4 size-8 shrink-0'
                  onClick={toggleSplitView}
                  aria-label='Toggle split view'
                >
                  <NavExpandIcon
                    className={cn('text-neutral-500', enableSplitView && 'rotate-180')}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent size='sm' side='left' align='center'>
                {enableSplitView ? 'Disable' : 'Enable'} Split View
              </TooltipContent>
            </Tooltip>
          )}
        </TabsList>

        {tabs.map((tab) => (
          <TabsContent asChild key={tab.id} value={tab.id}>
            <div
              className={cn(
                'relative flex h-full w-full grow flex-col items-center overflow-y-auto bg-neutral-25 px-6 pb-2 pt-2',
                tab.type === TAB_TYPE_CHAT && 'p-0 pb-4',
              )}
            >
              {renderTabContent(tab)}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </SortableContext>
  )
}

export { DraggableTabs }
